import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Row, Col, Typography, Image } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';

import { registerUser } from 'api/auth';
import { storeToken } from 'utils/auth';

const { Title, Paragraph } = Typography;

interface IRegisterFormValues {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
}

const Register: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const onFinish = async (values: IRegisterFormValues) => {
        setLoading(true);
        try {
            const result = await registerUser(values);
            storeToken(result.token, true);
            navigate('/tools');
        } catch (error) {
            console.error('Registration failed:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-row">
                <Row>
                    {/* Hero Section */}
                    <Col xs={24} lg={12} className="branding-section">
                        <div className="branding-content">
                            <div className="logo-container">
                                <Image
                                    src={`${process.env.PUBLIC_URL}/logo.png`}
                                    alt="Power Tools Logo"
                                    preview={false}
                                    className="logo-image"
                                />
                                <Title level={2} className="brand-name">Power Tools</Title>
                            </div>
                            <Title level={3} className="brand-tagline">Join Our Developer Community</Title>
                            <Paragraph className="brand-description">
                                Create your account to access our suite of tools designed to enhance your Power Platform development experience.
                            </Paragraph>
                        </div>
                    </Col>
                    <Col xs={24} lg={12} className="form-section">
                        <div className="form-container">
                            <Title level={2} className="form-title">Create Account</Title>
                            <Form
                                name="register"
                                onFinish={onFinish}
                                size="large"
                            >
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="firstName"
                                            rules={[{ required: true, message: 'Please input your first name!' }]}
                                        >
                                            <Input prefix={<UserOutlined />} placeholder="First name" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="lastName"
                                            rules={[{ required: true, message: 'Please input your last name!' }]}
                                        >
                                            <Input prefix={<UserOutlined />} placeholder="Last name" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    name="email"
                                    rules={[
                                        { required: true, message: 'Please input your email!' },
                                        { type: 'email', message: 'Please enter a valid email!' }
                                    ]}
                                >
                                    <Input prefix={<MailOutlined />} placeholder="Email" />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    rules={[
                                        { required: true, message: 'Please input your password!' },
                                        { min: 8, message: 'Password must be at least 8 characters!' }
                                    ]}
                                >
                                    <Input.Password prefix={<LockOutlined />} placeholder="Password" />
                                </Form.Item>

                                <Form.Item
                                    name="confirmPassword"
                                    dependencies={['password']}
                                    rules={[
                                        { required: true, message: 'Please confirm your password!' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('The two passwords do not match!'));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password prefix={<LockOutlined />} placeholder="Confirm password" />
                                </Form.Item>

                                <Form.Item>
                                    <Button type="primary" htmlType="submit" className="login-button" loading={loading} block>
                                        Create Account
                                    </Button>
                                </Form.Item>
                            </Form>
                            <div className="signup-prompt">
                                <Paragraph>Already have an account? </Paragraph>
                                <Link to="/auth/login">
                                    <Button type="link" className="signup-link">Sign in</Button>
                                </Link>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default Register;
